import type { ConsoleLoggerCategory, StorageClient, ConsoleLogger } from '../state/types';

// Monochromatic palette (from darkest to lightest)
// 13459c, 1557c0, 196bde, 2382f7, 4b9cf9, 77b6fb, a4cefc, cce4fd
const categoryTags: Record<ConsoleLoggerCategory, [string, string]> = {
  abTesting: ['AB testing', 'background-color: #4b9cf9; font-size: 12px; font-weight: 700;'],
  api: ['Api', 'background-color: #196bde; font-size: 12px; font-weight: 700;'],
  analytics: ['Analytics', 'background-color: #2382f7; font-size: 12px; font-weight: 700;'],
  general: ['', 'background-color: #13459c; font-size: 12px; font-weight: 700;'],
  state: ['State', 'background-color: #1557c0; font-size: 12px; font-weight: 700;'],
  storage: ['Storage', 'background-color: #77b6fb; font-size: 12px; font-weight: 700;'],
  logger: ['Logger', 'background-color: #a4cefc; font-size: 12px; font-weight: 700;'],
};
const messageStyle = 'font-size: 12px;';

const noop = () => undefined;
const wrapMessage = (category: ConsoleLoggerCategory, message: string) => {
  const categoryTag = categoryTags[category][0];

  return [`%c[Unicorn${categoryTag ? `/${categoryTag}` : ''}]%c ${message}`, categoryTags[category][1], messageStyle];
};

export const createConsoleLogger = ({ devMode, storage }: { devMode: boolean; storage: StorageClient }): ConsoleLogger => {
  const storedCategories = storage.createController<ConsoleLoggerCategory[]>('logger-enabled-categories');
  const readResult = storedCategories.get();
  const enabledCategories = readResult.status === 'success' ? readResult.value : ['general' as const];

  const logDebug: ConsoleLogger['debug'] = (category, message, data) =>
    enabledCategories.includes(category) ? console.info(...wrapMessage(category, message), ...(data ? [data] : [])) : noop;
  const logError: ConsoleLogger['error'] = (category, message, data) => console.error(...wrapMessage(category, message), ...(data ? [data] : []));
  const logWarning: ConsoleLogger['warning'] = (category, message, data) => console.warn(...wrapMessage(category, message), ...(data ? [data] : []));

  const toggleCategory: ConsoleLogger['toggleCategory'] = (category) => {
    if (enabledCategories.includes(category)) {
      enabledCategories.splice(enabledCategories.indexOf(category), 1);
      console.info(...wrapMessage('general', 'Logger category toggled'), { category, toggleResult: 'disabled' });
    } else {
      enabledCategories.push(category);
      console.info(...wrapMessage('general', 'Logger category toggled'), { category, toggleResult: 'enabled' });
    }

    storedCategories.set(enabledCategories);
  };

  if (devMode) {
    logDebug('general', 'Debugging enabled');

    return { debug: logDebug, error: logError, warning: logWarning, toggleCategory, enabledCategories };
  }

  return { debug: noop, error: logError, warning: noop, toggleCategory, enabledCategories };
};
